import { drawCanvasImage, drawRoundedRect } from '../../helpers';
import { ConnectorLineBtn, ConnectorLineConfig } from '../ConnectorLine';

/**
 * @method drawLine
 * @description Draw line
 * @param ctx canvas 2d context
 * @param points x and y coordinates
 */
export const drawLine = (
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
) => {
  const len = points.length;
  if (len === 1) {
    ctx.moveTo(points[0].x, points[0].y);
  } else if (len === 2) {
    ctx.bezierCurveTo(
      points[0].x,
      points[0].y,
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
    );
  } else if (len > 2) {
    let lastPoint = points[0];
    for (let i = 1; i < len; i += 1) {
      const point = points[i];
      const mid = { x: point.x, y: point.y };
      if (i < len - 1) {
        if (lastPoint.x !== point.x) {
          // horizontal line draw
          mid.x = (lastPoint.x + point.x) / 2;
        } else {
          // vertical line draw
          mid.y = (lastPoint.y + point.y) / 2;
        }
      }
      ctx.bezierCurveTo(
        lastPoint.x,
        lastPoint.y,
        lastPoint.x,
        lastPoint.y,
        mid.x,
        mid.y,
      );
      lastPoint = point;
    }
  }
};

/**
 * @method drawHorizontalConnector
 * @description Draw horizontal connector
 * @param ctx canvas context
 * @param fromX from X coordinate
 * @param fromY from Y coordinate
 * @param toX to X coordinate
 * @param toY to Y coordinate
 */
export const drawHorizontalConnector = ({
  ctx,
  fromX,
  fromY,
  toX,
  toY
}: {
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
}) => {
  const curveOffset = 30;
  const midX = (fromX + toX) / 2;
  const midY = (fromY + toY) / 2;
  if (fromX < toX - curveOffset * 2) {
    drawLine(ctx, [
      { x: midX, y: fromY },
      { x: midX, y: toY },
      { x: toX, y: toY },
    ]);
  } else {
    drawLine(ctx, [
      { x: fromX + curveOffset, y: fromY },
      { x: fromX + curveOffset, y: midY },
      { x: toX - curveOffset, y: midY },
      { x: toX - curveOffset, y: toY },
      { x: toX, y: toY },
    ]);
  }
};

/**
 * @method drawVerticalConnector
 * @description Draw vertical connector
 * @param ctx canvas context
 * @param fromX from X coordinate
 * @param fromY from Y coordinate
 * @param toX to X coordinate
 * @param toY to Y coordinate
 */
export const drawVerticalConnector = ({
  ctx,
  fromX,
  fromY,
  toX,
  toY
}: {
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
}) => {
  const curveOffset = 30;
  const midX = (fromX + toX) / 2;
  const midY = (fromY + toY) / 2;
  if (fromY < toY - curveOffset * 2) {
    drawLine(ctx, [
      { x: fromX, y: midY },
      { x: toX, y: midY },
      { x: toX, y: toY },
    ]);
  } else {
    drawLine(ctx, [
      { x: fromX, y: fromY + curveOffset },
      { x: midX, y: fromY + curveOffset },
      { x: midX, y: toY - curveOffset },
      { x: toX, y: toY - curveOffset },
      { x: toX, y: toY },
    ]);
  }
};

/**
 * @method drawVerticalToHorizontalConnector
 * @description Draw vertical to horizontal connector
 * @param ctx canvas context
 * @param fromX from X coordinate
 * @param fromY from Y coordinate
 * @param inVY from incoming vertical Y coordinate
 * @param outHX from outgoing horizontal X coordinate
 * @param toX to X coordinate
 * @param toY to Y coordinate
 */
export const drawVerticalToHorizontalConnector = ({
  ctx,
  fromX,
  fromY,
  inVY,
  outHX,
  toX,
  toY,
  clickX,
  clickY
}: {
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  outHX: number,
  inVY: number,
  clickX: number,
  clickY: number
}) => {
  const curveOffset = 30;
  let midX = (fromX + toX) / 2;
  let midY = (fromY + toY) / 2;
  if (toX < fromX + curveOffset || toY < fromY + curveOffset) {
    midX = (outHX + toX) / 2;
    if (midX > toX - curveOffset) {
      midX = toX - curveOffset;
    }
    midY = (fromY + inVY) / 2;
    if (midY < fromY + curveOffset) {
      midY = fromY + curveOffset;
    }
    drawLine(ctx, [
      { x: fromX, y: midY },
      { x: midX, y: midY },
      { x: midX, y: toY },
      { x: toX, y: toY },
    ]);

    if (toY < fromY + curveOffset) {
      clickX = midX;
      clickY = (toY + midY) / 2;
    } else {
      clickY = midY;
      clickX = (fromX + midX) / 2;
    }
  } else {
    drawLine(ctx, [
      { x: fromX, y: toY },
      { x: toX, y: toY },
    ]);

    clickY = toY;
  }

  return {
    clickX,
    clickY
  };
};

/**
 * @method drawHorizontalToVerticalConnector
 * @description Draw horizontal to vertical connector
 * @param ctx canvas context
 * @param fromX from X coordinate
 * @param fromY from Y coordinate
 * @param inHX from incoming horizontal X coordinate
 * @param outVY from outgoing vertical Y coordinate
 * @param toX to X coordinate
 * @param toY to Y coordinate
 */
export const drawHorizontalToVerticalConnector = ({
  ctx,
  fromX,
  fromY,
  inHX,
  outVY,
  toX,
  toY,
  clickX,
  clickY
}: {
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  outVY: number,
  inHX: number,
  clickX: number,
  clickY: number
}) => {
  const curveOffset = 30;
  let midX = (fromX + toX) / 2;
  let midY = (fromY + toY) / 2;
  if (toX < fromX + curveOffset || toY < fromY + curveOffset) {
    midY = (outVY + toY) / 2;
    if (midY > toY - curveOffset) {
      midY = toY - curveOffset;
    }
    midX = (fromX + inHX) / 2;
    if (midX < fromX + curveOffset) {
      midX = fromX + curveOffset;
    }
    drawLine(ctx, [
      { x: midX, y: fromY },
      { x: midX, y: midY },
      { x: toX, y: midY },
      { x: toX, y: toY },
    ]);

    if (toY < fromY + curveOffset) {
      clickX = midX;
      clickY = (fromY + midY) / 2;
    } else {
      clickY = midY;
      clickX = (toX + midX) / 2;
    }
  } else {
    drawLine(ctx, [
      { x: toX, y: fromY },
      { x: toX, y: toY },
    ]);

    clickX = toX;
  }

  return {
    clickX,
    clickY
  };
};

/**
 * @method drawIcon
 * @description draw Icon
 * @param ctx canvas context
 */
export const drawIcon = ({
  ctx,
  buttons,
  btnGroupPosition,
  textBound,
  hovering
}: {
  ctx: CanvasRenderingContext2D,
  buttons: ConnectorLineBtn[],
  btnGroupPosition: {
    left: number;
    top: number;
    right: number;
    bottom: number;
    height: number;
    width: number;
  },
  textBound: {
    l: number;
    r: number;
    t: number;
    b: number;
  },
  hovering: boolean
}) => {
  if (buttons.length > 0) {
    btnGroupPosition.right = textBound.r - 10;
    btnGroupPosition.bottom = textBound.t;
    btnGroupPosition.top = btnGroupPosition.bottom - btnGroupPosition.height;
    btnGroupPosition.left = btnGroupPosition.right - btnGroupPosition.width;

    if (hovering) {
      let btnIconLeft = btnGroupPosition.left;
      buttons.forEach((button) => {
        drawCanvasImage(ctx, button.image, btnIconLeft, btnGroupPosition.top, button.width, button.height);
        button.top = btnGroupPosition.top;
        button.left = btnIconLeft;
        button.bottom = button.top + button.height;
        button.right = button.left + button.width;
        btnIconLeft += button.width + 12;
      });
    }
  }
};

/**
 * @method drawBackground
 * @description Draws the background on a canvas.
 * @param {Object} options - The options for drawing the background.
 * @param {CanvasRenderingContext2D} options.ctx - The canvas rendering context.
 * @param {number} options.codX - The x-coordinate of the background.
 * @param {number} options.codY - The y-coordinate of the background.
 * @param {number} options.width - The width of the background.
 * @param {number} options.height - The height of the background.
 * @param {ConnectorLineConfig} options.config - The configuration for the connector line.
 * @returns {void}
 */
export const drawBackground = ({
  ctx,
  codX,
  codY,
  width,
  height,
  config
}: {
  ctx: CanvasRenderingContext2D,
  codX: number,
  codY: number,
  width: number,
  height: number,
  config: ConnectorLineConfig
}) => {
  ctx.fillStyle = config.textColor;
  drawRoundedRect(
    ctx,
    codX,
    codY,
    width,
    height,
    20
  );
  ctx.fill();
  ctx.fillStyle = 'white';
};
